<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useNotificationStore } from '../stores/notificationStore'
import NotificationCenterSidebar from '../components/NotificationCenterSidebar.vue'
import NotificationCenterToolbar from '../components/NotificationCenterToolbar.vue'
import NotificationCenterItem from '../components/NotificationCenterItem.vue'
import NotificationEmptyState from '../components/NotificationEmptyState.vue'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'

/**
 * Раскладка повторяет паттерн `src/views/apps/documents-new/index.vue` — drawer слева
 * + main справа, mobile-overlay для drawer.
 *
 * Поиск — фронтовая фильтрация через setSearch с дебаунсом 200мс (без серверного
 * запроса). Пагинация — cursor-based, как в таблицах: одна страница с бэка,
 * VDataTableFooter, page_size в localStorage.
 */
const store = useNotificationStore()
const { filteredItems, isLoading, currentPage, nextCursor, totalCount } = storeToRefs(store)
const { width } = useDisplay()
const { itemsPerPage } = useTableItemsPerPage('notifications', 25)

if (store.itemsPerPage !== itemsPerPage.value) {
  store.setItemsPerPage(itemsPerPage.value)
}

const pagerHeaders = [{ key: 'notification', title: '', sortable: false }]
const pageSizeOptions = [25, 50, 100]

const totalItems = computed(() => totalCount.value || 0)

const isMobile = computed(() => width.value < 1024)
const isSidebarOpen = ref(true)

watch(
  isMobile,
  mobile => {
    isSidebarOpen.value = !mobile
  },
  { immediate: true },
)

const searchQuery = ref('')

watchDebounced(searchQuery, value => {
  store.setSearch(value)
}, { debounce: 200 })

const hideNextButton = computed(() => {
  if (!nextCursor.value) return true
  if (totalItems.value > 0 && itemsPerPage.value > 0) {
    return currentPage.value >= Math.ceil(totalItems.value / itemsPerPage.value)
  }

  return false
})

onMounted(() => {
  store.fetchNotifications({ reset: true })
})

function onTableOptions(options: { page?: number; itemsPerPage?: number }): void {
  const page = Number(options.page || 1)
  const perPage = Number(options.itemsPerPage || 25)

  if (!pageSizeOptions.includes(perPage)) return

  if (perPage !== store.itemsPerPage) {
    itemsPerPage.value = perPage
    store.setItemsPerPage(perPage)

    return
  }

  if (page === store.currentPage) return

  if (page <= 1) {
    store.goToFirstPage()

    return
  }

  if (page === store.currentPage + 1) {
    store.goToNextPage()

    return
  }

  if (page === store.currentPage - 1) {
    store.goToPrevPage()
  }
}
</script>

<template>
  <div class="notif-center">
    <!-- Mobile overlay при открытом sidebar -->
    <div
      v-if="isMobile && isSidebarOpen"
      class="notif-center__overlay"
      @click="isSidebarOpen = false"
    />

    <!-- Sidebar (drawer 260px) -->
    <aside
      v-if="isSidebarOpen"
      class="notif-center__sidebar"
      :class="{ 'notif-center__sidebar--mobile': isMobile }"
    >
      <div
        v-if="isMobile"
        class="notif-center__sidebar-mobile-header d-flex align-center justify-space-between"
      >
        <span class="notif-center__sidebar-title">Фильтры</span>
        <IconBtn
          variant="text"
          size="small"
          @click="isSidebarOpen = false"
        >
          <VIcon icon="ri-close-line" />
        </IconBtn>
      </div>

      <NotificationCenterSidebar />
    </aside>

    <!-- Основная область -->
    <main class="notif-center__main">
      <!-- Поиск -->
      <div class="notif-center__search d-flex align-center gap-2">
        <IconBtn
          v-if="isMobile"
          variant="text"
          @click="isSidebarOpen = !isSidebarOpen"
        >
          <VIcon icon="ri-filter-3-line" />
        </IconBtn>

        <VTextField
          v-model="searchQuery"
          density="compact"
          placeholder="Поиск"
          variant="solo"
          flat
          hide-details
          prepend-inner-icon="ri-search-line"
          class="notif-center__search-field flex-grow-1"
        />
      </div>

      <!-- Toolbar -->
      <NotificationCenterToolbar />

      <!-- Список + пагинация как в таблицах -->
      <VDataTableServer
        :items="filteredItems"
        :items-length="totalItems"
        :headers="pagerHeaders"
        :page="currentPage"
        :items-per-page="itemsPerPage"
        :items-per-page-options="pageSizeOptions"
        :loading="isLoading"
        loading-text=""
        item-value="id"
        hide-default-header
        show-current-page
        class="notif-center__table flex-grow-1"
        @update:options="onTableOptions"
      >
        <template #item="{ item }">
          <tr>
            <td class="pa-0">
              <NotificationCenterItem :notification="item" />
            </td>
          </tr>
        </template>

        <template #loading>
          <VSkeletonLoader
            v-for="i in 5"
            :key="i"
            type="list-item-avatar-three-line"
          />
        </template>

        <template #no-data>
          <NotificationEmptyState size="full" />
        </template>

        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': hideNextButton }"
          >
            <VDataTableFooter
              show-current-page
              :items-per-page-options="pageSizeOptions"
            />
          </div>
        </template>
      </VDataTableServer>
    </main>
  </div>
</template>

<style scoped lang="scss">
.notif-center {
  display: flex;
  flex-direction: row !important;
  inline-size: 100%;
  max-inline-size: 100%;
  block-size: 100%;
  min-block-size: 400px;
  background: rgb(var(--v-theme-surface));
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(var(--v-theme-on-surface), 0.2);
  position: relative;

  &__overlay {
    position: fixed;
    inset: 0;
    background: rgba(var(--v-theme-on-surface), 0.5);
    z-index: 999;
  }

  &__sidebar {
    inline-size: 260px;
    background: rgb(var(--v-theme-surface));
    border-inline-end: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    overflow-y: auto;
    flex-shrink: 0;

    &--mobile {
      position: fixed;
      inset-block-start: 0;
      inset-inline-start: 0;
      inset-block-end: 0;
      z-index: 1000;
      box-shadow: 2px 0 8px rgba(var(--v-theme-on-surface), 0.15);
    }
  }

  &__sidebar-mobile-header {
    padding: 12px 16px;
    border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    background: rgb(var(--v-theme-surface));
    position: sticky;
    inset-block-start: 0;
    z-index: 1;
  }

  &__sidebar-title {
    font-size: 16px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  &__main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: rgb(var(--v-theme-surface));
    min-inline-size: 0;
  }

  &__search {
    padding: 12px 16px;
    border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }

  &__search-field {
    :deep(.v-field) {
      background: rgba(var(--v-theme-on-surface), 0.04);
      box-shadow: none;
      border: 0;
    }

    :deep(.v-field__input) {
      padding-block: 6px;
      font-size: 14px;
      color: rgba(var(--v-theme-on-surface), 0.87);
    }
  }

  &__table {
    flex: 1;
    min-block-size: 0;
    overflow: hidden;

    :deep(.v-table__wrapper) {
      flex: 1;
      overflow-y: auto;
    }

    :deep(.v-data-table__td),
    :deep(td) {
      padding: 0 !important;
      border: 0 !important;
      height: auto !important;
    }

    :deep(tr:hover) {
      background: transparent !important;
    }
  }
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }

  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}
</style>
